import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import classNames from "classnames";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DialogFrame } from "component/shared/dialog-frame";
import { keepOneSteps, removeDuplicateSteps, useFixRun } from "component/shared/mls-id-dialogs";
import { OutlinedButton } from "component/shared/outlined-button";
import { AgentMlsIds } from "model/agent-mls-ids";
import { FixFrame } from "../fix-frame";
import { keepOnePlan } from "./keep-one-plan";
import { styles } from "./style";

interface KeepOneProps extends WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	// The duplicate's entry; null when closed.
	entryId: number | null;
	// The toast once the fix lands, or a warning when it stopped partway.
	onSaved?: (message: string, severity?: "warning") => void;
	onClose: () => void;
}

// Keep one of a duplicate's Featured rows; the others are removed.
const Component: React.FC<KeepOneProps> = ({ agentMlsIds, entryId, onSaved, onClose, classes = {} }) => {
	const plan = useMemo(() => (entryId != null ? keepOnePlan(agentMlsIds, entryId) : null), [agentMlsIds, entryId]);
	const [keepId, setKeepId] = useState<number | null>(null);
	const [confirmingRemoveAll, setConfirmingRemoveAll] = useState(false);
	const { saving, error, setError, run, reset } = useFixRun(agentMlsIds);

	useEffect(() => {
		setKeepId(plan ? plan.suggested : null);
		setConfirmingRemoveAll(false);
		reset();
	}, [entryId]);

	const done = (message: string) => () => {
		onSaved?.(message);
		onClose();
	};
	const partial = (message: string) => {
		onSaved?.(message, "warning");
		onClose();
	};
	// Remove All is for one agent's ID. A value another agent also holds is a Review instead.
	const canRemoveAll = !!plan && !!removeDuplicateSteps(agentMlsIds, plan.entryId);

	// Remove All: every Featured row but one goes to "None", then the agent's MLS ID comes off their profile with it.
	const removeAll = async () => {
		if (plan) {
			const { entryId: id, mlsId, boardName } = plan;
			await run({
				plan: (current) => removeDuplicateSteps(current, id),
				context: { mlsId, boardName },
				onDone: done(`${mlsId} removed from Featured Listings.`),
				onPartial: partial,
			});
			// Closed after the writes, so a second click can't send them twice; a refusal shows in the dialog under it.
			setConfirmingRemoveAll(false);
		}
	};

	const save = () => {
		if (!plan || keepId === null) {
			setError("Choose which one to keep.");
			return;
		}
		const { entryId: id, mlsId } = plan;
		run({ plan: (current) => keepOneSteps(current, id, keepId), onDone: done(`Fixed. ${mlsId} is on Featured Listings once.`), onPartial: partial });
	};
	// The rows share their agent and MLS ID, so each radio's label leads with the row's place.

	return (
		<>
			<FixFrame
				open={!!plan}
				title="Remove Duplicate"
				titleId="keep-one-title"
				saveLabel="Keep Selected"
				saving={saving}
				error={error}
				onSave={save}
				onClose={onClose}
				leadingAction={canRemoveAll && (
					<Mui.Button className={classes.leading} startIcon={<Icons.Trash2 size={18} />} onClick={() => setConfirmingRemoveAll(true)} disabled={saving}>
						Remove All
					</Mui.Button>
				)}
			>
				{plan && (
					<>
						<Mui.Typography className={classes.prompt}>{plan.prompt}</Mui.Typography>
						<Mui.Table size="small" className={classes.table}>
							<Mui.TableHead>
								<Mui.TableRow>
									<Mui.TableCell>Agent MLS ID</Mui.TableCell>
									<Mui.TableCell>Office MLS ID</Mui.TableCell>
									{plan.tied && <Mui.TableCell>Added</Mui.TableCell>}
								</Mui.TableRow>
							</Mui.TableHead>
							<Mui.TableBody>
								{plan.rows.map((row, index) => (
									<Mui.TableRow key={row.clientBoardId} hover selected={keepId === row.clientBoardId} onClick={() => setKeepId(row.clientBoardId)}>
										<Mui.TableCell className={classes.pick}>
											<Mui.Radio
												size="small"
												checked={keepId === row.clientBoardId}
												onChange={() => setKeepId(row.clientBoardId)}
												inputProps={{ "aria-label": `Keep row ${index + 1} of ${plan.rows.length}, Office MLS ID ${row.alternateId ?? "None"}${row.added ? `, added ${row.added.toLowerCase()}` : ""}` }}
											/>
											{row.mlsId}
										</Mui.TableCell>
										<Mui.TableCell className={classNames(row.alternateId === null && classes.hint)}>{row.alternateId ?? "None"}</Mui.TableCell>
										{plan.tied && <Mui.TableCell>{row.added}</Mui.TableCell>}
									</Mui.TableRow>
								))}
							</Mui.TableBody>
						</Mui.Table>
					</>
				)}
			</FixFrame>
			{/* Remove All's confirmation, stacked over the dialog. */}
			<DialogFrame
				open={!!plan && confirmingRemoveAll}
				onClose={() => setConfirmingRemoveAll(false)}
				title={plan ? plan.removeAll.title : ""}
				titleId="keep-one-remove-all-title"
				actions={
					<>
						<OutlinedButton onClick={() => setConfirmingRemoveAll(false)} disabled={saving}>Cancel</OutlinedButton>
						<Mui.Button variant="contained" color="primary" onClick={removeAll} disabled={saving}>Remove All</Mui.Button>
					</>
				}
			>
				<Mui.Typography>{plan?.removeAll.text}</Mui.Typography>
			</DialogFrame>
		</>
	);
};

export const KeepOneDialog = withStyles(Component, styles);
